import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-cycle
import ProductIdentifierMaster from './ProductIdentifierMaster';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';

export const Context = React.createContext();
function ContextWrapper({ OpenReArrangeFunction, state, setState }) {
  const { t } = useTranslation('common');
  const {
    SystemMastersDetails: { productIdentifierList, identifierType },
    Users: { logInRights },
  } = useSelector((reducer) => reducer);
  const { CheckActionPermission } = useComponent();

  const MasterAddEditAccess = CheckActionPermission(logInRights, 'masters', 'productIdentifier', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'masters', 'productIdentifier', 'delete');
  const [cards, setCards] = useState([]);
  const [formState, setFormState] = useState({
    values: {},
    errors: {},
    brandErrors: {},
    isSubmitted: false,
    disableBtn: false,
    isLoading: false,
  });
  const [openCard, setOpenCard] = useState(false);
  const validateInput = useCallback(
    (value, id, field) => {
      if (field === 'name') {
        if (!value || value.trim().length === 0) {
          setFormState((prev) => ({
            ...prev,
            errors: { ...prev.errors, [id]: t('PRODUCT_IDENTIFIER.ENTER_PRODUCT_IDENTIFIER') },
          }));
          return false;
        }

        const doesExist = cards.find((obj) => obj.name.trim().toLowerCase() === value.trim().toLowerCase() && obj._id !== id);

        if (doesExist) {
          setFormState((prev) => ({
            ...prev,
            errors: { ...prev.errors, [id]: t('PRODUCT_IDENTIFIER.ENTRY_ALREADY_EXIST') },
          }));
          return false;
        }

        // Clear name error if validation passes
        setFormState((prev) => {
          const newErrors = { ...prev.errors };
          delete newErrors[id];
          return { ...prev, errors: newErrors };
        });
        return true;
      }

      if (field === 'brand') {
        const currentCard = cards.find((cardItem) => cardItem._id === id);
        const hasBrands = currentCard?.brandIds?.length > 0;

        if (!hasBrands) {
          setFormState((prev) => ({
            ...prev,
            brandErrors: { ...prev.brandErrors, [id]: t('PRODUCT_IDENTIFIER.BRAND_REQUIRED') },
          }));
          return false;
        }

        // Clear brand error if validation passes
        setFormState((prev) => {
          const newBrandErrors = { ...prev.brandErrors };
          delete newBrandErrors[id];
          return { ...prev, brandErrors: newBrandErrors };
        });
        return true;
      }

      return false;
    },
    [cards, t],
  );

  const updateSubmitState = useCallback((val) => {
    setFormState((prev) => ({
      ...prev,
      isSubmitted: val,
      values: {},
      errors: val ? prev.errors : {},
      brandErrors: val ? prev.brandErrors : {},
    }));
  }, []);

  const setFormValues = useCallback(
    (val, id, field = 'name') => {
      setFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [id]: val },
      }));

      // Only validate on blur or save, not on every change
      if (!formState.isSubmitted) return;
      validateInput(val, id, field);
    },
    [formState.isSubmitted, validateInput],
  );

  useEffect(() => {
    setCards(productIdentifierList);
    if (state?.rearrangeModal === 'productIdentifier') {
      setState((prev) => ({
        ...prev,
        rearrangeData: [...productIdentifierList],
      }));
    }
  }, [productIdentifierList, state?.rearrangeModal]);

  const updateArray = useCallback((val, _id) => {
    if (_id) {
      setCards((prev) => prev.map((obj) => (obj._id === _id ? { ...obj, ...val } : obj)));
      return null;
    }
    setCards((prev) => [...prev, { _id, ...val }]);
  }, []);

  const addNewItem = useCallback(() => {
    setOpenCard(true);
    setCards((prev) => [...prev, { _id: `_id${prev.length}`, name: '', isEditable: true, isDeleted: false, isActive: true, isChecked: true }]);
  }, []);

  const removeFromArray = useCallback((_id) => {
    setCards((prev) => prev.filter((obj) => obj._id !== _id));
    setFormState({
      values: {},
      errors: {},
      isSubmitted: false,
      disableBtn: false,
      isLoading: false,
    });
  }, []);

  const value = useMemo(
    () => ({
      cards,
      updateArray,
      addNewItem,
      removeFromArray,
      updateSubmitState,
      formState,
      setFormValues,
      MasterAddEditAccess,
      MasterDeleteAccess,
      openCard,
      setOpenCard,
      identifierType,
      OpenReArrangeFunction,
      setFormState,
    }),
    [
      cards,
      updateArray,
      addNewItem,
      removeFromArray,
      updateSubmitState,
      formState,
      setFormValues,
      MasterAddEditAccess,
      MasterDeleteAccess,
      openCard,
      setOpenCard,
      identifierType,
      OpenReArrangeFunction,
      setFormState,
    ],
  );

  return (
    <Context.Provider value={value}>
      <ProductIdentifierMaster />
    </Context.Provider>
  );
}

export default ContextWrapper;
